import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

export default function HeroSection() {
  const { t } = useTranslation();
  const [currentSlide, setCurrentSlide] = useState(0);
  const [familyImageIndex, setFamilyImageIndex] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);

  const familyImages = [
    {
      url: 'https://public.readdy.ai/ai/img_res/46c826a1a2b76c106360f872a72826a6.jpg',
      zoom: 'scale-100'
    },
    {
      url: 'https://readdy.ai/api/search-image?query=close%20up%20hands%20of%20grandmother%20and%20child%20holding%20organic%20biscuits%20together%2C%20tea%20cup%20on%20rustic%20wooden%20table%2C%20warm%20golden%20light%2C%20emotional%20connection%2C%20soft%20focus%20background%2C%20intimate%20detail%20shot%2C%20warm%20color%20palette%2C%20professional%20food%20photography&width=1920&height=1080&seq=fam002&orientation=landscape',
      zoom: 'scale-110'
    },
    {
      url: 'https://readdy.ai/api/search-image?query=happy%20multigenerational%20italian%20family%20laughing%20together%2C%20grandfather%20with%20grandchildren%20enjoying%20tea%20time%2C%20organic%20biscuits%20on%20elegant%20plate%2C%20bright%20natural%20light%2C%20joyful%20atmosphere%2C%20warm%20home%20interior%2C%20candid%20moment%2C%20lifestyle%20photography&width=1920&height=1080&seq=fam003&orientation=landscape',
      zoom: 'scale-105'
    },
    {
      url: 'https://readdy.ai/api/search-image?query=beautiful%20arrangement%20of%20organic%20tea%20biscuits%20on%20ceramic%20plate%2C%20steaming%20teapot%20and%20cups%2C%20family%20hands%20reaching%20for%20biscuits%2C%20warm%20afternoon%20light%2C%20cozy%20home%20setting%2C%20soft%20bokeh%20background%2C%20inviting%20composition%2C%20food%20styling%20photography&width=1920&height=1080&seq=fam004&orientation=landscape',
      zoom: 'scale-100'
    }
  ];

  const slides = [
    {
      type: 'image',
      image: 'https://readdy.ai/api/search-image?query=premium%20organic%20spices%20and%20herbs%20arranged%20artistically%20on%20rustic%20wooden%20surface%2C%20vibrant%20natural%20colors%2C%20cinnamon%20sticks%2C%20star%20anise%2C%20dried%20herbs%2C%20soft%20natural%20lighting%2C%20warm%20earthy%20tones%2C%20minimalist%20composition%2C%20high%20quality%20food%20photography%2C%20simple%20clean%20background&width=1920&height=1080&seq=hero001&orientation=landscape',
      title: 'Spezie Biologiche di Alta Qualità',
      subtitle: 'Dalla natura alla vostra tavola, per esperienze di gusto autentiche',
      cta: 'Scopri le Spezie'
    },
    {
      type: 'family',
      title: 'Momenti Preziosi da Condividere',
      subtitle: 'I nostri biscotti biologici uniscono le generazioni in momenti di pura gioia',
      cta: 'Scopri i Biscotti'
    }
  ];

  // Auto-advance main slides
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
    }, 8000);
    return () => clearInterval(timer);
  }, []);

  // Auto-advance family images with smooth transitions
  useEffect(() => {
    if (currentSlide === 1) {
      const timer = setInterval(() => {
        setIsTransitioning(true);
        setTimeout(() => {
          setFamilyImageIndex((prev) => (prev + 1) % familyImages.length);
          setIsTransitioning(false);
        }, 500);
      }, 3000);
      return () => clearInterval(timer);
    }
  }, [currentSlide]);

  const goToSlide = (index: number) => {
    setCurrentSlide(index);
  };

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % slides.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);
  };

  return (
    <div className="relative h-screen overflow-hidden">
      {/* Slides Container */}
      <div className="relative w-full h-full">
        {slides.map((slide, index) => (
          <div
            key={index}
            className={`absolute inset-0 transition-opacity duration-1000 ${
              currentSlide === index ? 'opacity-100 z-10' : 'opacity-0 z-0'
            }`}
          >
            {slide.type === 'image' ? (
              // Regular Image Slide
              <>
                <div className="absolute inset-0">
                  <img
                    src={slide.image}
                    alt={slide.title}
                    className="w-full h-full object-cover object-top"
                  />
                </div>
                <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/40"></div>
              </>
            ) : (
              // Family Multi-Image Slide with Cinematic Effects
              <div className="absolute inset-0 bg-black">
                {familyImages.map((img, imgIndex) => (
                  <div
                    key={imgIndex}
                    className={`absolute inset-0 transition-all duration-1000 ${
                      familyImageIndex === imgIndex
                        ? 'opacity-100 z-10'
                        : 'opacity-0 z-0'
                    } ${isTransitioning ? 'blur-sm' : 'blur-0'}`}
                  >
                    <img
                      src={img.url}
                      alt={`Family moment ${imgIndex + 1}`}
                      className={`w-full h-full object-cover object-center transition-transform duration-[3000ms] ${
                        familyImageIndex === imgIndex && !isTransitioning
                          ? img.zoom
                          : 'scale-100'
                      }`}
                    />
                  </div>
                ))}
                <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/30 to-black/50 z-20"></div>
                
                {/* Cinematic Progress Bar */}
                <div className="absolute bottom-24 left-1/2 transform -translate-x-1/2 z-30 flex gap-2">
                  {familyImages.map((_, imgIndex) => (
                    <div
                      key={imgIndex}
                      className="w-12 h-1 bg-white/30 rounded-full overflow-hidden"
                    >
                      <div
                        className={`h-full bg-white rounded-full transition-all ${
                          imgIndex === familyImageIndex
                            ? 'w-full duration-[3000ms]'
                            : imgIndex < familyImageIndex
                            ? 'w-full duration-0'
                            : 'w-0 duration-0'
                        }`}
                      ></div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Content Overlay */}
            <div className="absolute inset-0 flex items-center justify-center z-30">
              <div className="text-center text-white px-4 max-w-4xl w-full">
                <h1 className="text-5xl md:text-7xl font-bold mb-6 animate-fade-in">
                  {slide.title}
                </h1>
                <p className="text-xl md:text-2xl mb-8 font-light animate-fade-in-delay">
                  {slide.subtitle}
                </p>
                <button className="bg-white text-gray-900 px-8 py-4 rounded-full text-lg font-semibold hover:bg-gray-100 transition-all duration-300 hover:scale-105 shadow-lg whitespace-nowrap cursor-pointer animate-fade-in-delay-2">
                  {slide.cta}
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Navigation Arrows */}
      <button
        onClick={prevSlide}
        className="absolute left-6 top-1/2 transform -translate-y-1/2 z-40 bg-white/20 hover:bg-white/30 backdrop-blur-sm text-white w-12 h-12 rounded-full flex items-center justify-center transition-all duration-300 hover:scale-110 cursor-pointer"
        aria-label="Previous slide"
      >
        <i className="ri-arrow-left-s-line text-2xl"></i>
      </button>
      <button
        onClick={nextSlide}
        className="absolute right-6 top-1/2 transform -translate-y-1/2 z-40 bg-white/20 hover:bg-white/30 backdrop-blur-sm text-white w-12 h-12 rounded-full flex items-center justify-center transition-all duration-300 hover:scale-110 cursor-pointer"
        aria-label="Next slide"
      >
        <i className="ri-arrow-right-s-line text-2xl"></i>
      </button>

      {/* Slide Indicators */}
      <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 z-40 flex gap-3">
        {slides.map((_, index) => (
          <button
            key={index}
            onClick={() => goToSlide(index)}
            className={`w-3 h-3 rounded-full transition-all duration-300 cursor-pointer ${
              currentSlide === index
                ? 'bg-white w-8'
                : 'bg-white/50 hover:bg-white/70'
            }`}
            aria-label={`Go to slide ${index + 1}`}
          ></button>
        ))}
      </div>

      {/* Scroll Indicator */}
      <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 z-30 animate-bounce hidden md:block">
        <i className="ri-arrow-down-line text-white text-3xl"></i>
      </div>
    </div>
  );
}